import { ActionMenu, type ActionMenuItemProps } from "@/components/ActionMenu.tsx";
import { WButton } from "@/components/button/WButton.tsx";
import { intersperse } from "@warrenio/utils/collections/intersperse";
import { useState, type ReactNode } from "react";
import type { Key } from "react-aria-components";

export type MultiSelectItem<TId extends Key = string> = ActionMenuItemProps & { id: TId };

export interface AdminMultiSelectProps<T extends MultiSelectItem> {
    items: T[];
    value: string[];
    onChange: (value: string[]) => void;

    canUnselectAll?: boolean;
    /** Label to display when no items are selected */
    label?: ReactNode;
}

export function AdminMultiSelect<T extends MultiSelectItem>({
    items,
    value,
    onChange,
    canUnselectAll = true,
    label = "Please select",
}: AdminMultiSelectProps<T>) {
    const [checkedIds, setCheckedIds] = useState<string[]>(value);

    function onToggle(id: string) {
        if (!canUnselectAll && checkedIds.length < 2 && checkedIds.includes(id)) return;

        setCheckedIds((prev) => {
            const isChecked = prev.includes(id);
            const updated = isChecked ? prev.filter((v) => v !== id) : [...prev, id];
            onChange(updated);
            return updated;
        });
    }

    function setAllChecked(isChecked: boolean) {
        const updated = isChecked ? items.map((item) => item.id) : [];
        setCheckedIds(updated);
        onChange(updated);
    }

    const checkedItems = items.filter((entry) => checkedIds.includes(entry.id));

    return (
        <ActionMenu
            onAction={({ id }) => onToggle(id)}
            selectedKeys={checkedIds}
            items={items}
            selectionMode="multiple"
            header={
                <div className="flex-1 flex gap-1rem justify-between">
                    <WButton color="primary" variant="ghost" size="xs" action={() => setAllChecked(true)}>
                        Select All
                    </WButton>
                    {canUnselectAll && (
                        <>
                            <div>|</div>
                            <WButton color="primary" variant="ghost" size="xs" action={() => setAllChecked(false)}>
                                Unselect All
                            </WButton>
                        </>
                    )}
                </div>
            }
        >
            <WButton color="default" variant="basic" size="md" ariaLabel="Select items" action={undefined}>
                <span className="text-ellipsis max-w-15rem">
                    {checkedItems.length
                        ? items.length === checkedItems.length
                            ? "All"
                            : intersperse(
                                  ", ",
                                  checkedItems.map((entry) => entry.title),
                              )
                        : label}
                </span>
            </WButton>
        </ActionMenu>
    );
}
